import { Disclosure } from '@headlessui/react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { useLocalStorageState } from '@components/component-helpers';
import { IconButton } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconCondition } from '@components/icons/condition';
import { IconCredit } from '@components/icons/credit';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconPause } from '@components/icons/pause';
import { IconPlay } from '@components/icons/play';
import { IconResume } from '@components/icons/resume';
import { IconSidemenuLeftClose } from '@components/icons/sidemenu-left-close';
import { IconSidemenuLeftOpen } from '@components/icons/sidemenu-left-open';
import { IconSkip } from '@components/icons/skip';
import { IconTextFormatBullet } from '@components/icons/text-format-bullet';
import { IconTravellerStatusCompleted } from '@components/icons/traveller-status-completed';
import { IconTravellerStatusInProgress } from '@components/icons/traveller-status-in-progress';
import { IconTravellerStatusWaitingToBeStarted } from '@components/icons/traveller-status-waiting-to-be-started';
import { RelativeSpinner } from '@components/loading-spinner';
import { StatusPiechartCircle } from '@components/piechart-circle/piechart-circle';
import { Separator } from '@components/separator';
import { Shapes } from '@components/shapes';
import { Stack } from '@components/stack';
import { Tag, TagProps } from '@components/tag';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import { useIsOutOfCredits } from 'api/authentication/hooks';
import {
  usePauseJourneyTraveller,
  useResumeJourneyTraveller,
  useStartJourneyTraveller,
} from 'api/traveller-journeys/hooks';
import { TravellerJourneyDetails } from 'api/traveller-journeys/types';
import { TravellerJourneyAction, TravellerJourneyStatus } from 'api/types';

const transition = { type: 'just', duration: 0.5, ease: 'easeOut' };

const containerVariants = {
  open: {
    width: pxToRem(320),
    height: 'auto',
    transition,
  },
  closed: {
    width: pxToRem(102),
    height: 'auto',
    transition,
  },
};

export const StatusCard = ({
  actions,
  traveller,
}: {
  actions: TravellerJourneyAction[];
  traveller: TravellerJourneyDetails;
}) => {
  const { t } = useScopedTranslate(locale);
  const isOutOfCredits = useIsOutOfCredits();
  const [collapsed, setCollapsed] = useLocalStorageState({
    key: 'traveller-journey-status-card-collapsed',
    defaultValue: false,
  });
  const data = { travellerJourneyId: traveller.shortId, journeyId: traveller.journey.shortId };
  const startJourneyTravellerMutation = useStartJourneyTraveller(data);
  const pauseJourneyTravellerMutation = usePauseJourneyTraveller(data);
  const resumeJourneyTravellerMutation = useResumeJourneyTraveller(data);
  const completedActions = actions.filter(
    (action) =>
      action.status === 'completed' ||
      (action.status === 'halted' && action.action === 'condition' && !!action.result?.dateStarted)
  );

  const inProcessActions = actions.filter((action) => action.status === 'in_progress');
  const skippedActions = actions.filter((action) => action.isSkipped && action.hasFlowReached);
  const toBeSkippedActions = actions.filter(
    (action) => action.isSkipped && action.status !== 'halted'
  );

  const waitingActions = actions.filter(
    (action) => action.status === 'waiting_to_be_started' && !action.isSkipped
  );

  const haltedActions = actions.filter(
    (action) =>
      action.status === 'halted' && (action.action !== 'condition' || !action.result?.dateStarted)
  );

  const remainingActionsCount =
    actions.length -
    completedActions.length -
    inProcessActions.length -
    waitingActions.length -
    haltedActions.length -
    toBeSkippedActions.length;

  const progress: { status: string; color: Color; value: number; icon: IconProps }[] = [
    {
      status: t('completed'),
      color: 'elementGreen100',
      icon: <IconTravellerStatusCompleted />,
      value: completedActions.length,
    },
    {
      status: t('inProgress'),
      color: 'elementOrange100',
      icon: <IconTravellerStatusInProgress />,
      value: inProcessActions.length,
    },
    {
      status: t('waitingToBeStarted'),
      color: 'elementIndigo100',
      icon: <IconTravellerStatusWaitingToBeStarted />,
      value: waitingActions.length,
    },
    {
      status: t('skipped'),
      color: 'elementPurple50',
      icon: <IconSkip color="elementPurple50" />,
      value: skippedActions.length,
    },
    {
      status: t('conditionNotMet'),
      color: 'elementPurple50',
      icon: <IconCondition color="elementPurple50" />,
      value: haltedActions.length,
    },
  ];
  const progressWithRemaining = progress.concat({
    status: t('remaining'),
    color: 'elementPurple50',
    icon: <IconTextFormatBullet color="elementPurple50" />,
    value: remainingActionsCount,
  });
  const isLoading =
    startJourneyTravellerMutation.isLoading ||
    pauseJourneyTravellerMutation.isLoading ||
    resumeJourneyTravellerMutation.isLoading;
  const status = traveller.status;
  if (actions.length === 0) {
    return null;
  }

  const finalStatus = isOutOfCredits && status === 'in_progress' ? 'paused' : status;

  const ActionButton = () => {
    if (finalStatus === 'paused') {
      return (
        <Button
          variant="secondary"
          stopPropagation
          iconLeft={<IconResume />}
          text={t('resume')}
          isDisabled={isOutOfCredits}
          onClick={() => resumeJourneyTravellerMutation.mutate({})}
        />
      );
    }
    if (finalStatus === 'not_started') {
      return (
        <Button
          variant="primary-blue"
          stopPropagation
          iconLeft={<IconPlay />}
          text={t('start')}
          isDisabled={isOutOfCredits}
          onClick={() => startJourneyTravellerMutation.mutate({})}
        />
      );
    }
    if (finalStatus === 'in_progress') {
      return (
        <Button
          variant="secondary"
          stopPropagation
          iconLeft={<IconPause />}
          text={t('pause')}
          isDisabled={isOutOfCredits}
          onClick={() => pauseJourneyTravellerMutation.mutate({})}
        />
      );
    }
    return null;
  };

  return (
    <Disclosure defaultOpen={!collapsed}>
      {({ open }) => (
        <Container
          variants={containerVariants}
          animate={open ? 'open' : 'closed'}
          $isExpanded={open}
          style={{ backgroundColor: theme.color.backgroundPrimary }}
        >
          <Card
            space={6}
            variant={isOutOfCredits ? 'gray' : 'white'}
            style={isOutOfCredits ? { backgroundColor: theme.color.uiTrans10 } : {}}
          >
            <Stack space={4} align={open ? undefined : 'center'}>
              <Stack orientation="horizontal" justify="space-between">
                {open && (
                  <Stack space={2} orientation="horizontal" align="center">
                    <IconJourneyItem color="elementPurple80" />
                    <Text text={t('travellerJourney')} font="bold16" ellipsis />
                  </Stack>
                )}
                <Disclosure.Button
                  onClick={() => setCollapsed(!collapsed)}
                  as={IconButton}
                  variant="ghost-purple"
                  icon={open ? <IconSidemenuLeftClose /> : <IconSidemenuLeftOpen />}
                />
              </Stack>
              <Separator />
              {isLoading ? (
                <RelativeSpinner />
              ) : (
                <>
                  {open ? (
                    <StyledStack orientation="horizontal" align="center" justify="space-between">
                      <Stack space={2} align="center" orientation="horizontal">
                        <StatusPiechartCircle
                          status={finalStatus}
                          sections={progressWithRemaining}
                          size={3}
                        />
                        <TravellerJourneyTag status={finalStatus} />
                      </Stack>
                      <ActionButton />
                    </StyledStack>
                  ) : (
                    <StatusPiechartCircle
                      status={finalStatus}
                      sections={progressWithRemaining}
                      size={3}
                    />
                  )}
                </>
              )}
              {open ? (
                <>
                  {traveller.dateStarted && (
                    <StyledStack space={2} align="center" orientation="horizontal">
                      <TextStatusIndicator variant="pending" text={t('started')} />
                      <Text text="•" font="regular12" color="textSecondary" />
                      <Text
                        text={abbreviateDate(traveller.dateStarted, 'en', 'datetime')}
                        font="regular12"
                        color="textSecondary"
                        ellipsis
                      />
                    </StyledStack>
                  )}
                  {traveller.dateCompleted && (
                    <StyledStack space={2} align="center" orientation="horizontal">
                      <TextStatusIndicator variant="success" text={t('completed')} />
                      <Text text="•" font="regular12" color="textSecondary" />
                      <Text
                        text={abbreviateDate(traveller.dateCompleted, 'en', 'datetime')}
                        font="regular12"
                        color="textSecondary"
                        ellipsis
                      />
                    </StyledStack>
                  )}
                </>
              ) : (
                <>
                  {traveller.dateStarted && (
                    <CollapsedStatus>
                      <Shapes.Circle size={8} color="elementOrange100" />
                    </CollapsedStatus>
                  )}
                  {traveller.dateCompleted && (
                    <CollapsedStatus>
                      <Shapes.Circle size={8} color="elementGreen100" />
                    </CollapsedStatus>
                  )}
                </>
              )}
              <Separator />
              <Stack space={1}>
                {progress
                  .filter((item) => item.value > 0)
                  .map(({ status, icon, value }) => (
                    <ProgressItem
                      isExpanded={open}
                      key={status}
                      status={status}
                      icon={icon}
                      value={value}
                    />
                  ))}
              </Stack>
              <Separator />
              <ProgressItem
                status={t('remaining')}
                icon={<IconTextFormatBullet color="elementPurple50" />}
                value={remainingActionsCount}
                isExpanded={open}
                isRemaining
              />
              <Separator />
              <ProgressItem
                status={t('totalCreditUsage')}
                icon={<IconCredit color="elementPurple50" />}
                value={traveller.creditsUsed}
                isExpanded={open}
              />
            </Stack>
          </Card>
        </Container>
      )}
    </Disclosure>
  );
};

const TravellerJourneyTag = ({ status }: { status: TravellerJourneyStatus }) => {
  const { t } = useScopedTranslate(locale);
  const tagProps: Record<TravellerJourneyStatus, Omit<TagProps, 'size'>> = {
    completed: { variant: 'green', text: t('completed') },
    in_progress: { variant: 'orange', text: t('inProgress') },
    not_started: { variant: 'purple', text: t('notStarted') },
    paused: { variant: 'purple', text: t('paused') },
  };
  return <Tag size="small" {...tagProps[status]} />;
};

const Container = styled(motion.div)<{ $isExpanded: boolean }>(({ $isExpanded }) => ({
  width: $isExpanded ? pxToRem(320) : 'auto',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 1,
  margin: pxToRem(24),
  overflow: 'hidden',
}));

const StyledStack = styled(Stack)({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'clip',
});

const CollapsedStatus = styled.div({
  height: pxToRem(20),
  display: 'flex',
  alignItems: 'center',
});

const ProgressItem = ({
  status,
  icon,
  value,
  isExpanded = false,
  isRemaining = false,
}: {
  status: string;
  icon: IconProps;
  value: number;
  isExpanded?: boolean;
  isRemaining?: boolean;
}) => (
  <Stack
    orientation="horizontal"
    align="center"
    justify={isExpanded ? 'space-between' : undefined}
    space={1}
  >
    <Stack align="center" orientation="horizontal" space={2}>
      {icon}
      {isExpanded && <Text font="semibold14" text={status} ellipsis />}
    </Stack>
    <Badge size="md" variant={isRemaining ? 'secondary-green' : 'secondary'}>
      <Text font="semibold12" color="textSecondary" text={value.toString()} />
    </Badge>
  </Stack>
);

const locale = {
  en: {
    travellerJourney: 'Traveller Journey',
    start: 'Start',
    pause: 'Pause',
    resume: 'Resume',
    notStarted: 'Not Started',
    started: 'Started',
    completed: 'Completed',
    inProgress: 'In Progress',
    paused: 'Paused',
    waitingToBeStarted: 'Waiting to be started',
    conditionNotMet: 'Condition not met',
    remaining: 'Remaining',
    totalCreditUsage: 'Total credit usage',
    skipped: 'Skipped',
  },
};
